import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import {
  FULFILL_EPSILON,
  isLineFullyFulfilled,
  orderedBasis,
  remainingQty,
  remainingWeight,
} from "@/features/sales-orders/constants";
import type { SalesOrderFulfillment, SalesOrderLine } from "@/features/sales-orders/types";
import type { InventoryBatchListRow } from "@/features/inventory/types";

function money(v: unknown) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

export type FulfillAllocationInput = {
  sales_order_line_id: string;
  inventory_batch_id: string;
  quantity: number;
  weight: number;
};

export type FulfillSalesOrderResult = {
  fulfillment_count: number;
  so_status: "partial" | "closed" | "in_progress";
};

export function openFulfillLines(lines: SalesOrderLine[]) {
  return lines.filter((l) => !isLineFullyFulfilled(l));
}

export function useMatchingBatchesForLine(line: {
  material_id: string;
  material_grade_id: string | null;
  shape_size_id: string | null;
}) {
  return useQuery({
    queryKey: [
      "fulfillment_batches",
      line.material_id,
      line.material_grade_id ?? "any",
      line.shape_size_id ?? "any",
    ],
    enabled: isSupabaseConfigured && Boolean(line.material_id),
    queryFn: async (): Promise<InventoryBatchListRow[]> => {
      let q = supabase
        .from("inventory_batches")
        .select(
          `*,
          materials(name),
          material_grades(name),
          shape_sizes(size_label),
          stock_locations(code, name),
          uoms(code)`,
        )
        .eq("material_id", line.material_id)
        .eq("batch_status", "in_stock")
        .eq("quality_status", "passed")
        .gt("weight", 0)
        .order("received_at", { ascending: true });

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? [])
        .map((row) => {
          const raw = row as Record<string, unknown>;
          const material = raw.materials as { name?: string } | null;
          const grade = raw.material_grades as { name?: string } | null;
          const shape = raw.shape_sizes as { size_label?: string } | null;
          const loc = raw.stock_locations as { code?: string; name?: string } | null;
          const uom = raw.uoms as { code?: string } | null;
          return {
            id: String(raw.id),
            batch_no: String(raw.batch_no ?? ""),
            material_id: String(raw.material_id),
            material_grade_id: (raw.material_grade_id as string | null) ?? null,
            shape_size_id: (raw.shape_size_id as string | null) ?? null,
            location_id: String(raw.location_id),
            quantity: money(raw.quantity),
            weight: money(raw.weight),
            uom_id: (raw.uom_id as string | null) ?? null,
            thickness: raw.thickness == null ? null : money(raw.thickness),
            width: raw.width == null ? null : money(raw.width),
            length: raw.length == null ? null : money(raw.length),
            wt_per_m: raw.wt_per_m == null ? null : money(raw.wt_per_m),
            purchase_order_id: (raw.purchase_order_id as string | null) ?? null,
            purchase_order_line_id: (raw.purchase_order_line_id as string | null) ?? null,
            vendor_id: (raw.vendor_id as string | null) ?? null,
            batch_status: raw.batch_status as InventoryBatchListRow["batch_status"],
            quality_status: raw.quality_status as InventoryBatchListRow["quality_status"],
            inspected_by: (raw.inspected_by as string | null) ?? null,
            inspected_at: (raw.inspected_at as string | null) ?? null,
            qc_notes: (raw.qc_notes as string | null) ?? null,
            received_at: String(raw.received_at ?? raw.created_at),
            notes: (raw.notes as string | null) ?? null,
            created_by: (raw.created_by as string | null) ?? null,
            created_at: String(raw.created_at),
            updated_at: String(raw.updated_at),
            mother_batch_id: (raw.mother_batch_id as string | null) ?? null,
            last_process_type_id: (raw.last_process_type_id as string | null) ?? null,
            process_path: (raw.process_path as string | null) ?? null,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
            location_code: loc?.code ?? null,
            location_name: loc?.name ?? null,
            uom_code: uom?.code ?? null,
            vendor_code: null,
            vendor_name: null,
            po_number: null,
          } satisfies InventoryBatchListRow;
        })
        .filter((b) => {
          if (line.material_grade_id && b.material_grade_id && b.material_grade_id !== line.material_grade_id) {
            return false;
          }
          if (line.shape_size_id && b.shape_size_id && b.shape_size_id !== line.shape_size_id) {
            return false;
          }
          return true;
        });
    },
  });
}

export function useSalesOrderFulfillments(soId?: string) {
  return useQuery({
    queryKey: ["sales_order_fulfillments", soId],
    enabled: isSupabaseConfigured && Boolean(soId),
    queryFn: async (): Promise<SalesOrderFulfillment[]> => {
      const { data, error } = await supabase
        .from("sales_order_fulfillments")
        .select(
          `*,
          inventory_batches(batch_no),
          sales_order_lines(line_no, materials(name))`,
        )
        .eq("sales_order_id", soId!)
        .order("fulfilled_at", { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const batch = raw.inventory_batches as { batch_no?: string } | null;
        const line = raw.sales_order_lines as {
          line_no?: number;
          materials?: { name?: string } | null;
        } | null;
        return {
          id: String(raw.id),
          sales_order_id: String(raw.sales_order_id),
          sales_order_line_id: String(raw.sales_order_line_id),
          inventory_batch_id: String(raw.inventory_batch_id),
          quantity: money(raw.quantity),
          weight: money(raw.weight),
          fulfilled_at: String(raw.fulfilled_at),
          created_by: (raw.created_by as string | null) ?? null,
          created_at: String(raw.created_at),
          batch_no: batch?.batch_no ?? null,
          material_name: line?.materials?.name ?? null,
          line_no: line?.line_no,
        };
      });
    },
  });
}

export function useFulfillSalesOrder() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      salesOrderId,
      allocations,
    }: {
      salesOrderId: string;
      allocations: FulfillAllocationInput[];
    }): Promise<FulfillSalesOrderResult> => {
      if (!isSupabaseConfigured) throw new Error("Supabase is not configured");

      const active = allocations.filter((a) => money(a.weight) > 0 || money(a.quantity) > 0);
      if (!active.length) throw new Error("Add at least one allocation with weight or quantity");

      const { data: soRaw, error: soErr } = await supabase
        .from("sales_orders")
        .select(
          `*,
          sales_order_lines(*)`,
        )
        .eq("id", salesOrderId)
        .single();
      if (soErr) throw soErr;

      const so = soRaw as Record<string, unknown>;
      const status = String(so.status);
      if (!["confirmed", "in_progress", "partial"].includes(status)) {
        throw new Error("Sales order is not open for fulfillment");
      }

      const lines = ((so.sales_order_lines as Record<string, unknown>[]) ?? []).map((l) => ({
        id: String(l.id),
        line_no: Number(l.line_no),
        material_id: String(l.material_id),
        qty: money(l.qty),
        weight: money(l.weight),
        fulfilled_qty: money(l.fulfilled_qty),
        fulfilled_weight: money(l.fulfilled_weight),
      }));

      const lineById = new Map(lines.map((l) => [l.id, { ...l }]));

      for (const alloc of active) {
        const line = lineById.get(alloc.sales_order_line_id);
        if (!line) throw new Error("Invalid sales order line");
        const wt = money(alloc.weight);
        const qty = money(alloc.quantity);
        if (wt <= 0 && qty <= 0) throw new Error("Enter weight or quantity");

        const remWt = remainingWeight(line);
        const remQty = remainingQty(line);
        const useWt = wt > 0 ? wt : qty;
        if (useWt > remWt + FULFILL_EPSILON) {
          throw new Error(`Line ${line.line_no}: allocation exceeds remaining ${remWt}`);
        }
        if (line.qty > 0 && qty > 0 && qty > remQty + FULFILL_EPSILON) {
          throw new Error(`Line ${line.line_no}: qty exceeds remaining ${remQty}`);
        }

        const { data: batch, error: bErr } = await supabase
          .from("inventory_batches")
          .select("id, weight, quantity, location_id, batch_status, material_id")
          .eq("id", alloc.inventory_batch_id)
          .single();
        if (bErr) throw bErr;
        if (batch.batch_status !== "in_stock") throw new Error("Batch is not in stock");
        if (String(batch.material_id) !== line.material_id) {
          throw new Error("Batch material does not match order line");
        }
        const batchWt = money(batch.weight);
        const batchQty = money(batch.quantity);
        const takeWt = wt > 0 ? wt : qty;
        if (takeWt > batchWt + FULFILL_EPSILON) {
          throw new Error(`Batch ${alloc.inventory_batch_id}: insufficient weight`);
        }
        let takeQty = qty;
        if (takeQty <= 0 && line.qty > 0 && line.weight > 0 && takeWt > 0) {
          takeQty = Math.round((takeWt / line.weight) * line.qty);
        }
        if (takeQty > batchQty + FULFILL_EPSILON && batchQty > 0) {
          takeQty = batchQty;
        }

        const { data: fulfillment, error: fErr } = await supabase
          .from("sales_order_fulfillments")
          .insert({
            sales_order_id: salesOrderId,
            sales_order_line_id: alloc.sales_order_line_id,
            inventory_batch_id: alloc.inventory_batch_id,
            quantity: takeQty,
            weight: takeWt,
            created_by: user?.id ?? null,
          })
          .select("id")
          .single();
        if (fErr) throw fErr;

        const newBatchWt = Math.max(0, batchWt - takeWt);
        const newBatchQty = Math.max(0, batchQty - takeQty);
        const consumed = newBatchWt <= FULFILL_EPSILON && newBatchQty <= FULFILL_EPSILON;

        const { error: batchUpdErr } = await supabase
          .from("inventory_batches")
          .update({
            weight: newBatchWt,
            quantity: newBatchQty,
            ...(consumed ? { batch_status: "consumed" } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq("id", alloc.inventory_batch_id);
        if (batchUpdErr) throw batchUpdErr;

        const { error: movErr } = await supabase.from("inventory_movements").insert({
          inventory_batch_id: alloc.inventory_batch_id,
          kind: "issue",
          quantity_delta: -takeQty,
          weight_delta: -takeWt,
          quantity_after: newBatchQty,
          weight_after: newBatchWt,
          from_location_id: batch.location_id as string,
          source_type: "sales_order_fulfillment",
          source_id: fulfillment.id,
          notes: `SO fulfillment line ${line.line_no}`,
          created_by: user?.id ?? null,
        });
        if (movErr) throw movErr;

        line.fulfilled_weight += takeWt;
        if (line.qty > 0) line.fulfilled_qty += takeQty;

        const fully = isLineFullyFulfilled({
          qty: line.qty,
          weight: line.weight,
          fulfilled_qty: line.fulfilled_qty,
          fulfilled_weight: line.fulfilled_weight,
        });

        const { error: lineErr } = await supabase
          .from("sales_order_lines")
          .update({
            fulfilled_qty: line.fulfilled_qty,
            fulfilled_weight: line.fulfilled_weight,
            updated_at: new Date().toISOString(),
          })
          .eq("id", line.id);
        if (lineErr) throw lineErr;

        lineById.set(line.id, line);
        void fully;
      }

      const updatedLines = [...lineById.values()];
      const allFull = updatedLines.every((l) =>
        isLineFullyFulfilled({
          qty: l.qty,
          weight: l.weight,
          fulfilled_qty: l.fulfilled_qty,
          fulfilled_weight: l.fulfilled_weight,
        }),
      );
      const anyFulfilled = updatedLines.some((l) => l.fulfilled_weight > FULFILL_EPSILON);
      const soStatus: FulfillSalesOrderResult["so_status"] = allFull
        ? "closed"
        : anyFulfilled
          ? "partial"
          : "in_progress";

      const { error: soUpdErr } = await supabase
        .from("sales_orders")
        .update({
          status: soStatus,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", salesOrderId);
      if (soUpdErr) throw soUpdErr;

      return { fulfillment_count: active.length, so_status: soStatus };
    },
    onSuccess: async (result, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["sales_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["sales_orders", vars.salesOrderId] });
      await queryClient.invalidateQueries({ queryKey: ["sales_order_fulfillments", vars.salesOrderId] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["fulfillment_batches"] });
      toast.success(
        `${result.fulfillment_count} allocation(s) posted · SO ${result.so_status}`,
      );
    },
    onError: (e: Error) => toast.error(e.message || "Fulfillment failed"),
  });
}

export { remainingWeight, remainingQty, orderedBasis };
